import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BankStore, InsuranceStore } from '../../core/stores/base.store';
import { Bank, Insurance } from '../../core/models';
import { formatDateAsYYYYMMDD, parseYYYYMMDDToDate } from 'app/core/utils/date.utils';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { NotificationUtils } from '../../core/utils';
import { LoanUtils } from '../../core/utils/loan.utils';

@Component({
  selector: 'app-insurance-premium-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    NumberFormatDirective
  ],
  templateUrl: './insurance-premium-payment-dialog.component.html',
  styleUrls: ['./insurance-premium-payment-dialog.component.css']
})
export class InsurancePremiumPaymentDialogComponent implements OnInit {
  paymentForm!: FormGroup;
  loading = false;
  
  private bankStore = inject(BankStore);
  private insuranceStore = inject(InsuranceStore);
  private fb = inject(FormBuilder);
  
  banks = this.bankStore.entities;
  
  constructor(
    public dialogRef: MatDialogRef<InsurancePremiumPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { insurance: Insurance }
  ) {}
  
  ngOnInit(): void {
    // Initialize the payment form
    const premium = LoanUtils.parseFormattedNumber(this.data.insurance.premium);
    
    // Calculate the default paid_to_date (one year after current paid_to_date)
    let defaultPaidToDate: Date;
    
    if (this.data.insurance.paid_to_date) {
      // Start with the current paid_to_date
      if (typeof this.data.insurance.paid_to_date === 'string') {
        const parsedDate = parseYYYYMMDDToDate(this.data.insurance.paid_to_date);
        defaultPaidToDate = parsedDate ? new Date(parsedDate) : new Date();
      } else {
        defaultPaidToDate = new Date(this.data.insurance.paid_to_date);
      }
      
      // If the current paid_to_date is in the past, use today as the starting point
      const today = new Date();
      if (defaultPaidToDate < today) {
        defaultPaidToDate = today;
      }
    } else {
      // If no paid_to_date exists, use today
      defaultPaidToDate = new Date();
    }
    
    // Add one year to the date
    defaultPaidToDate.setFullYear(defaultPaidToDate.getFullYear() + 1);
    
    this.paymentForm = this.fb.group({
      insurance_id: [this.data.insurance.id],
      date: [new Date(), Validators.required],
      amount: [premium.toFixed(2), [Validators.required, Validators.min(0.01)]],
      bank_id: [null],
      paid_to_date: [defaultPaidToDate, Validators.required],
      payFromBank: [true]
    });
    
    // Add conditional validation for bank_id based on payFromBank value
    this.paymentForm.get('payFromBank')?.valueChanges.subscribe(value => {
      const bankIdControl = this.paymentForm.get('bank_id');
      if (value) {
        bankIdControl?.setValidators(Validators.required);
      } else {
        bankIdControl?.clearValidators();
      }
      bankIdControl?.updateValueAndValidity();
    });
    
    // Initialize the validation
    const payFromBank = this.paymentForm.get('payFromBank')?.value;
    if (payFromBank) {
      this.paymentForm.get('bank_id')?.setValidators(Validators.required);
      this.paymentForm.get('bank_id')?.updateValueAndValidity();
    }
  }
  
  /**
   * Handle form submission to record a premium payment
   */
  onSubmit(): void {
    if (this.paymentForm.invalid) {
      return;
    }
    
    this.loading = true;
    
    try {
      // Get the payment date and format it using the utility function
      const rawPaymentDate = this.paymentForm.get('date')?.value;
      const paymentDate = new Date(rawPaymentDate);
      const formattedPaymentDate = formatDateAsYYYYMMDD(paymentDate);
      
      // Parse the amount using the utility function
      const amount = LoanUtils.parseFormattedNumber(this.paymentForm.get('amount')?.value);
      
      // Get the paid_to_date and format it using the utility function
      const rawPaidToDate = this.paymentForm.get('paid_to_date')?.value;
      const paidToDate = new Date(rawPaidToDate);
      const formattedPaidToDate = formatDateAsYYYYMMDD(paidToDate);
      
      // Create the transaction object only if payFromBank is checked
      let transaction = null;
      if (this.paymentForm.get('payFromBank')?.value) {
        transaction = {
          insurance_id: this.paymentForm.get('insurance_id')?.value,
          transaction_date: formattedPaymentDate,
          amount: -amount, // Negative amount for expense
          bank_id: this.paymentForm.get('bank_id')?.value,
          category: 'Insurance Premium Payment'
        };
      }
      
      // Close the dialog with the result
      this.dialogRef.close({
        transaction: transaction,
        paidToDate: formattedPaidToDate
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      const notificationUtils = NotificationUtils.getInstance();
      notificationUtils.showError('Failed to process payment. Please try again.');
      this.loading = false;
    }
  }
  
  /**
   * Cancel the payment dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }
}
